// pkg125 — admin prop: Client Modules workspace renders this page with adminApi I/O; Submit hidden in admin mode (D-124e); PDF download via admin endpoint — delivered 2026-07-27
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { adminApi } from '../utils/adminApi'; // pkg125 — admin mode
import { useFirmSettings } from '../contexts/FirmSettingsContext';

// ──────────────────────────────────────────────────────────────────────────────
// Option lists. Kept short and practical; the attorney can clean up free-form
// answers if a court clerk wants something more specific.
// ──────────────────────────────────────────────────────────────────────────────

const SEX_OPTIONS = ['Male', 'Female'];

const RACE_OPTIONS = [
  'White',
  'Black or African American',
  'American Indian or Alaska Native',
  'Asian',
  'Native Hawaiian or Other Pacific Islander',
  'Two or More Races',
  'Other',
  'Unknown',
];

const YN_OPTIONS = ['Yes', 'No', 'Unknown'];

const MARITAL_OPTIONS = [
  'Single', 'Married', 'Separated', 'Divorced', 'Widowed', 'Unknown',
];

const GRADE_OPTIONS = [
  'Pre-K', 'Kindergarten',
  '1st', '2nd', '3rd', '4th', '5th',
  '6th', '7th', '8th',
  '9th', '10th', '11th', '12th',
  'Not in school',
];

// ──────────────────────────────────────────────────────────────────────────────
// Empty record templates
// ──────────────────────────────────────────────────────────────────────────────

const EMPTY_CHILD = {
  name: '', race: '', sex: '', dob: '', hispanic_origin: '', ssn: '',
  school: '', grade: '', special_ed: '',
  birthplace: '', address: '',
  lives_with: '', custodian: '', custodian_phone: '', custodian_address: '',
};

const EMPTY_PARENT = {
  name: '', address: '', race: '', sex: '', dob: '', hispanic_origin: '', ssn: '',
  home_phone: '', cell_phone: '', employer: '', work_phone: '',
  marital_status: '', drivers_license: '',
};

const EMPTY_PETITIONER = {
  who: 'other',     // 'mother' | 'father' | 'both' | 'other'
  name: '', relationship: '', marital_status: '', address: '',
  race: '', sex: '', dob: '', hispanic_origin: '', ssn: '',
  home_phone: '', cell_phone: '', employer: '', work_phone: '',
  drivers_license: '',
};

const EMPTY = {
  date: '',
  children: [{ ...EMPTY_CHILD }],
  mother: { ...EMPTY_PARENT },
  father: { ...EMPTY_PARENT },
  petitioner: { ...EMPTY_PETITIONER },
};

// ──────────────────────────────────────────────────────────────────────────────
// Small reusable input components
// ──────────────────────────────────────────────────────────────────────────────

function Field({ label, children, hint }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {children}
      {hint && (
        <div style={{ fontSize: 11, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function Text({ value, onChange, placeholder, type = 'text' }) {
  return <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''} />;
}

function Select({ value, onChange, options, placeholder = '— Select —' }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Child editor — used for both add and edit
// ──────────────────────────────────────────────────────────────────────────────

function ChildEditor({ child, onChange, onCancel, onSave, index }) {
  function set(key, val) { onChange({ ...child, [key]: val }); }
  return (
    <div style={{ background: '#F5F1E8', border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 12, fontWeight: 600 }}>
        {typeof index === 'number' ? `Child ${index + 1}` : 'New Child'}
      </div>
      <Field label="Full Name (First, Middle, Last)">
        <Text value={child.name} onChange={v => set('name', v)} placeholder="e.g. Emma Marie Smith" />
      </Field>
      <div className="form-row">
        <Field label="Race"><Select value={child.race} onChange={v => set('race', v)} options={RACE_OPTIONS} /></Field>
        <Field label="Sex"><Select value={child.sex} onChange={v => set('sex', v)} options={SEX_OPTIONS} /></Field>
        <Field label="Date of Birth"><Text type="date" value={child.dob} onChange={v => set('dob', v)} /></Field>
      </div>
      <div className="form-row">
        <Field label="Hispanic Origin?" hint="The PDF has no fillable Y/N field — the attorney marks this manually.">
          <Select value={child.hispanic_origin} onChange={v => set('hispanic_origin', v)} options={YN_OPTIONS} />
        </Field>
        <Field label="Social Security Number" hint="Required by the court. Transmitted securely.">
          <Text value={child.ssn} onChange={v => set('ssn', v)} placeholder="123-45-6789" />
        </Field>
      </div>
      <div className="form-row">
        <Field label="School"><Text value={child.school} onChange={v => set('school', v)} placeholder="School name" /></Field>
        <Field label="Grade"><Select value={child.grade} onChange={v => set('grade', v)} options={GRADE_OPTIONS} /></Field>
        <Field label="Enrolled in Special Ed?" hint="The PDF has no fillable Y/N field — marked manually.">
          <Select value={child.special_ed} onChange={v => set('special_ed', v)} options={YN_OPTIONS} />
        </Field>
      </div>
      <div className="form-row">
        <Field label="City & State of Birth">
          <Text value={child.birthplace} onChange={v => set('birthplace', v)} placeholder="e.g. Chattanooga, TN" />
        </Field>
        <Field label="Child's Address">
          <Text value={child.address} onChange={v => set('address', v)} placeholder="Where the child currently lives" />
        </Field>
      </div>
      <Field label="Person & Relationship Where Child Lives" hint="e.g. 'Aunt Mary Smith — maternal aunt'">
        <Text value={child.lives_with} onChange={v => set('lives_with', v)} />
      </Field>
      <div className="form-row">
        <Field label="Legal Custodian of Child"><Text value={child.custodian} onChange={v => set('custodian', v)} /></Field>
        <Field label="Custodian's Phone"><Text type="tel" value={child.custodian_phone} onChange={v => set('custodian_phone', v)} placeholder="(423) 555-0100" /></Field>
      </div>
      <Field label="Legal Custodian's Address">
        <Text value={child.custodian_address} onChange={v => set('custodian_address', v)} />
      </Field>
      <div className="btn-row">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" onClick={onSave}>Save Child</button>
      </div>
    </div>
  );
}

function ChildSummaryRow({ child, index, onEdit, onDelete }) {
  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: 12, marginBottom: 10, background: '#fff', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {child.name || `Child ${index + 1}`}
        </div>
        <div style={{ fontSize: 12, color: '#666', fontFamily: 'Arial, sans-serif' }}>
          {[child.sex, child.dob, child.school && `${child.school}${child.grade ? ', ' + child.grade : ''}`].filter(Boolean).join(' • ') || 'Details not yet entered'}
        </div>
      </div>
      <button onClick={onEdit}
        style={{ background: 'none', border: '1px solid #0F1838', borderRadius: 3, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontFamily: 'Arial, sans-serif' }}>
        Edit
      </button>
      <button onClick={onDelete}
        style={{ background: 'none', border: '1px solid #B85042', color: '#B85042', borderRadius: 3, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontFamily: 'Arial, sans-serif' }}>
        Delete
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Parent section (mother or father — same fields)
// ──────────────────────────────────────────────────────────────────────────────

function ParentSection({ title, value, onChange }) {
  function set(key, val) { onChange({ ...value, [key]: val }); }
  return (
    <>
      <div className="section-head">{title}</div>
      <div className="card">
        <Field label="Full Name (First, Middle, Last)">
          <Text value={value.name} onChange={v => set('name', v)} />
        </Field>
        <Field label="Address">
          <Text value={value.address} onChange={v => set('address', v)} />
        </Field>
        <div className="form-row">
          <Field label="Race"><Select value={value.race} onChange={v => set('race', v)} options={RACE_OPTIONS} /></Field>
          <Field label="Sex"><Select value={value.sex} onChange={v => set('sex', v)} options={SEX_OPTIONS} /></Field>
          <Field label="Date of Birth"><Text type="date" value={value.dob} onChange={v => set('dob', v)} /></Field>
        </div>
        <div className="form-row">
          <Field label="Hispanic Origin?" hint="The PDF has no fillable Y/N field — marked manually.">
            <Select value={value.hispanic_origin} onChange={v => set('hispanic_origin', v)} options={YN_OPTIONS} />
          </Field>
          <Field label="Social Security Number" hint="Required by the court. Transmitted securely.">
            <Text value={value.ssn} onChange={v => set('ssn', v)} placeholder="123-45-6789" />
          </Field>
        </div>
        <div className="form-row">
          <Field label="Home Phone"><Text type="tel" value={value.home_phone} onChange={v => set('home_phone', v)} /></Field>
          <Field label="Cell Phone"><Text type="tel" value={value.cell_phone} onChange={v => set('cell_phone', v)} /></Field>
        </div>
        <div className="form-row">
          <Field label="Employer"><Text value={value.employer} onChange={v => set('employer', v)} /></Field>
          <Field label="Work Phone"><Text type="tel" value={value.work_phone} onChange={v => set('work_phone', v)} /></Field>
          <Field label="Marital Status"><Select value={value.marital_status} onChange={v => set('marital_status', v)} options={MARITAL_OPTIONS} /></Field>
        </div>
        <Field label="Driver License # and State Issuing License">
          <Text value={value.drivers_license} onChange={v => set('drivers_license', v)} placeholder="e.g. 123456789 TN" />
        </Field>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────────────

// pkg125 — `admin` prop ({ clientId }): rendered by the Client Modules
// workspace with adminApi I/O; Submit hidden (D-124e); the PDF download uses
// the admin endpoint.
export default function JuvenileInfoPage({ admin = null }) {
  const { firm } = useFirmSettings();
  const io = admin ? {
    get:      () => adminApi.getClientJuvenileInfo(admin.clientId),
    save:     (data) => adminApi.saveClientJuvenileInfo(admin.clientId, data),
    download: () => adminApi.downloadJuvenileInfo(admin.clientId),
  } : {
    get:      () => api.getJuvenileInfo(),
    save:     (data) => api.saveJuvenileInfo(data),
    download: () => api.downloadJuvenileInfo(),
  };
  const [form, setForm]         = useState(EMPTY);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [status, setStatus]     = useState('');
  const [error, setError]       = useState('');
  const [editingChildIndex, setEditingChildIndex] = useState(null); // number | 'new'
  const [draftChild, setDraftChild] = useState({ ...EMPTY_CHILD });

  useEffect(() => {
    io.get().then(rec => {
      if (rec?.data) {
        setForm({
          ...EMPTY,
          ...rec.data,
          children: Array.isArray(rec.data.children) && rec.data.children.length
            ? rec.data.children
            : [{ ...EMPTY_CHILD }],
          mother:     { ...EMPTY_PARENT,     ...(rec.data.mother || {}) },
          father:     { ...EMPTY_PARENT,     ...(rec.data.father || {}) },
          petitioner: { ...EMPTY_PETITIONER, ...(rec.data.petitioner || {}) },
        });
      }
      if (rec?.submitted_at) setSubmitted(true);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  function setTop(key, val) { setForm(f => ({ ...f, [key]: val })); }
  function setMother(v)     { setForm(f => ({ ...f, mother: v })); }
  function setFather(v)     { setForm(f => ({ ...f, father: v })); }
  function setPetitioner(v) { setForm(f => ({ ...f, petitioner: v })); }

  function startAddChild() {
    setDraftChild({ ...EMPTY_CHILD });
    setEditingChildIndex('new');
  }
  function startEditChild(i) {
    setDraftChild({ ...form.children[i] });
    setEditingChildIndex(i);
  }
  function cancelChildEdit() {
    setEditingChildIndex(null);
    setDraftChild({ ...EMPTY_CHILD });
  }
  function saveChild() {
    setForm(f => {
      const children = [...f.children];
      if (editingChildIndex === 'new') children.push({ ...draftChild });
      else children[editingChildIndex] = { ...draftChild };
      return { ...f, children };
    });
    cancelChildEdit();
  }
  function deleteChild(i) {
    setForm(f => {
      const children = f.children.filter((_, idx) => idx !== i);
      return { ...f, children: children.length ? children : [{ ...EMPTY_CHILD }] };
    });
  }

  async function handleSave() {
    setSaving(true); setStatus(''); setError('');
    try {
      await io.save(form);
      setStatus('Progress saved.');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleSubmit() {
    setSubmitting(true); setStatus(''); setError('');
    try {
      const valid = (form.children || []).filter(c => c.name && c.name.trim());
      if (!valid.length) {
        throw new Error('Please add at least one child with a name before submitting.');
      }
      const result = await api.submitJuvenileInfo(form);
      setSubmitted(true);
      setStatus(result.message || 'Submitted to your attorney.');
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  async function handleDownload() {
    try {
      const blob = await io.download();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Juvenile_Court_Info.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); }
  }

  if (loading) return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;

  const who = form.petitioner.who || 'other';
  const showFullPetitioner = who === 'other';

  return (
    <div>
      <h1 className="page-title">Juvenile Court Info Form</h1>
      <p className="page-subtitle">
        This form is required for filings in juvenile court (custody and dependency-and-neglect cases).
        Fill in as much as you know — your attorney will review everything before filing. Your Social
        Security Number is required by the court and is transmitted securely.
      </p>

      {submitted && (
        <div className="alert alert-success">
          ✓ This form has been submitted to your attorney. You may still update and resubmit if anything changes.
        </div>
      )}
      {status && <div className="alert alert-success">{status}</div>}
      {error  && <div className="alert alert-error">{error}</div>}

      {/* DATE */}
      <div className="section-head">Filing Date</div>
      <div className="card">
        <Field label="Date" hint="The date this form is being completed.">
          <Text type="date" value={form.date} onChange={v => setTop('date', v)} />
        </Field>
      </div>

      {/* CHILDREN */}
      <div className="section-head">Children</div>
      <div className="card">
        <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginTop: 0, marginBottom: 12 }}>
          Add one entry per child the petition is being filed for. A separate PDF will be generated for each child.
        </p>

        {form.children.map((c, i) =>
          editingChildIndex === i ? (
            <ChildEditor key={i} child={draftChild} index={i}
              onChange={setDraftChild} onCancel={cancelChildEdit} onSave={saveChild} />
          ) : (
            <ChildSummaryRow key={i} child={c} index={i}
              onEdit={() => startEditChild(i)} onDelete={() => deleteChild(i)} />
          )
        )}

        {editingChildIndex === 'new' && (
          <ChildEditor child={draftChild}
            onChange={setDraftChild} onCancel={cancelChildEdit} onSave={saveChild} />
        )}

        {editingChildIndex === null && (
          <button className="btn btn-sm" onClick={startAddChild} style={{ marginTop: 8 }}>
            + Add Another Child
          </button>
        )}
      </div>

      {/* MOTHER */}
      <ParentSection title="Mother's Information" value={form.mother} onChange={setMother} />

      {/* FATHER */}
      <ParentSection title="Father's Information" value={form.father} onChange={setFather} />

      {/* PETITIONER */}
      <div className="section-head">Petitioner's Information</div>
      <div className="card">
        <Field label="Who is the petitioner?" hint="If the petitioner is the mother and/or father, the petitioner section is skipped to avoid duplication.">
          <select value={who} onChange={e => setPetitioner({ ...form.petitioner, who: e.target.value })}>
            <option value="mother">The Mother (see Mother section above)</option>
            <option value="father">The Father (see Father section above)</option>
            <option value="both">Both Mother and Father</option>
            <option value="other">Someone else (fill out below)</option>
          </select>
        </Field>

        {showFullPetitioner && (
          <>
            <div className="form-row">
              <Field label="Petitioner's Full Name (First, Middle, Last)">
                <Text value={form.petitioner.name} onChange={v => setPetitioner({ ...form.petitioner, name: v })} />
              </Field>
              <Field label="Relationship to Child">
                <Text value={form.petitioner.relationship} onChange={v => setPetitioner({ ...form.petitioner, relationship: v })}
                  placeholder="e.g. Grandmother, Maternal Aunt, Guardian" />
              </Field>
            </div>
            <Field label="Address">
              <Text value={form.petitioner.address} onChange={v => setPetitioner({ ...form.petitioner, address: v })} />
            </Field>
            <div className="form-row">
              <Field label="Race"><Select value={form.petitioner.race} onChange={v => setPetitioner({ ...form.petitioner, race: v })} options={RACE_OPTIONS} /></Field>
              <Field label="Sex"><Select value={form.petitioner.sex} onChange={v => setPetitioner({ ...form.petitioner, sex: v })} options={SEX_OPTIONS} /></Field>
              <Field label="Date of Birth"><Text type="date" value={form.petitioner.dob} onChange={v => setPetitioner({ ...form.petitioner, dob: v })} /></Field>
            </div>
            <div className="form-row">
              <Field label="Hispanic Origin?" hint="The PDF has no fillable Y/N field — marked manually.">
                <Select value={form.petitioner.hispanic_origin} onChange={v => setPetitioner({ ...form.petitioner, hispanic_origin: v })} options={YN_OPTIONS} />
              </Field>
              <Field label="Marital Status">
                <Select value={form.petitioner.marital_status} onChange={v => setPetitioner({ ...form.petitioner, marital_status: v })} options={MARITAL_OPTIONS} />
              </Field>
              <Field label="Social Security Number" hint="Required by the court.">
                <Text value={form.petitioner.ssn} onChange={v => setPetitioner({ ...form.petitioner, ssn: v })} placeholder="123-45-6789" />
              </Field>
            </div>
            <div className="form-row">
              <Field label="Home Phone"><Text type="tel" value={form.petitioner.home_phone} onChange={v => setPetitioner({ ...form.petitioner, home_phone: v })} /></Field>
              <Field label="Cell Phone"><Text type="tel" value={form.petitioner.cell_phone} onChange={v => setPetitioner({ ...form.petitioner, cell_phone: v })} /></Field>
            </div>
            <div className="form-row">
              <Field label="Employer"><Text value={form.petitioner.employer} onChange={v => setPetitioner({ ...form.petitioner, employer: v })} /></Field>
              <Field label="Work Phone"><Text type="tel" value={form.petitioner.work_phone} onChange={v => setPetitioner({ ...form.petitioner, work_phone: v })} /></Field>
            </div>
            <Field label="Driver License # and State Issuing License">
              <Text value={form.petitioner.drivers_license} onChange={v => setPetitioner({ ...form.petitioner, drivers_license: v })} placeholder="e.g. 123456789 TN" />
            </Field>
          </>
        )}

        {!showFullPetitioner && (
          <div className="alert alert-info" style={{ marginTop: 12 }}>
            Petitioner information will be pulled from the {who === 'both' ? 'Mother and Father sections' : `${who.charAt(0).toUpperCase() + who.slice(1)} section`} above.
          </div>
        )}
      </div>

      {/* ACTIONS */}
      <div className="btn-row">
        <button className="btn" onClick={handleSave} disabled={saving || editingChildIndex !== null}>
          {saving ? <><span className="spinner" />Saving...</> : 'Save Progress'}
        </button>
        <button className="btn" onClick={handleDownload} disabled={editingChildIndex !== null}>
          Download Filled PDF
        </button>
        {!admin && (
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || editingChildIndex !== null}>
            {submitting ? <><span className="spinner" />Submitting...</> : 'Submit to Attorney'}
          </button>
        )}
      </div>
      {editingChildIndex !== null && (
        <p style={{ fontSize: 12, color: '#B85042', fontFamily: 'Arial, sans-serif', marginTop: 12 }}>
          Save or cancel the current child entry before saving the whole form.
        </p>
      )}
      {!admin && (
        <p style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: 12 }}>
          Submitting will save your information and email completed PDFs (one per child) to {firm?.client_contact_email || 'your attorney'}.
          You can update and resubmit at any time.
        </p>
      )}
    </div>
  );
}
